import { useState, useEffect } from 'react'
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
  Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Textarea,
  Badge,
  Input,
  Button
} from '@creanizer/ui'
import { X, Plus, Trash2, Upload, FileText } from 'lucide-react'
import type { ManualCharacterInput } from '@creanizer/types'
import { SelectWithCustom } from './SelectWithCustom'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useEnemyStore } from '../../../../stores/enemyStore'

interface ManualInputTabsProps {
  value: ManualCharacterInput
  onChange: (value: ManualCharacterInput) => void
  imageType: 'portrait' | 'illustration' | 'background' | 'object'
  uploadedImage?: string | null
  imagePreview?: string | null
  isAnalyzing?: boolean
  onImageUpload?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onImageAnalyze?: () => void
  onImageClear?: () => void
}

// 表示用ラベルと値のマッピング
const GENDER_MAP = {
  '男性': 'male',
  '女性': 'female',
  'その他': 'other'
}

// プリセット定義
const PRESETS: any = {
  gender: ['男性', '女性', 'その他'],
  ageGroup: ['子供', '少年少女', '青年', '大人', '高齢者'],
  bodyType: ['細身', '標準', 'がっしり', 'ふくよか', '筋肉質', 'カスタム'],
  
  // 髪型
  hairFront: [
    'ぱっつん', '流し前髪', 'センター分け', 'ワンレン', 'アシメ前髪', 
    'オン眉', '触角', 'M字前髪', 'シースルーバング', 'かきあげ前髪',
    'ベビーバング', 'カーテンバング', 'フェザーバング', '斜め前髪',
    '重め前髪', 'ジグザグ前髪', 'カスタム'
  ],
  hairStyle: [
    // 基本の長さ
    'ロング', 'セミロング', 'ミディアム', 'ショート', 'ベリーショート',
    // ボブ系
    'ボブ', 'ショートボブ', 'ロングボブ', '前下がりボブ', 'マッシュボブ',
    // ポニーテール系
    'ポニーテール', 'ローポニー', 'ハイポニー', 'サイドポニー', '一つ結び',
    // ツインテール系
    'ツインテール', 'ローツイン', 'ハイツイン',
    // お団子系
    'お団子', 'ハーフアップお団子', 'ツインお団子',
    // アップスタイル
    'ツーサイドアップ', 'ハーフアップ', 'フルアップ', 'シニヨン',
    // 編み込み系
    '三つ編み', 'フィッシュボーン', '編み込み', 'ツイン三つ編み',
    // 特殊系
    'ウルフカット', 'レイヤーカット', 'ワンレングス', 'マッシュ',
    'ツーブロック', 'アンダーカット', 'モヒカン', 'ドレッドヘア',
    'アフロ', 'カスタム'
  ],
  hairTexture: [
    'ストレート', 'ウェーブ', 'カール', '無造作', 'パーマ',
    'くせ毛', '天然パーマ', '縮毛', 'カスタム'
  ],
  hairColor: ['黒髪', '茶髪', '金髪', '銀髪', '赤髪', '青髪', '緑髪', '紫髪', 'ピンク髪', 'オレンジ髪', '白髪', 'カスタム'],
  hairColorEffect: ['グラデーション', 'メッシュ', 'カスタム'],
  
  // 目
  eyeShape: ['普通', 'ツリ目', 'タレ目', '丸目', '細目', 'アーモンド型', '猫目', 'カスタム'],
  eyeSize: ['大きい', '普通', '小さい'],
  eyeColor: ['黒', '茶色', '青', '緑', '灰色', '赤', '紫', '金', 'ヘテロクロミア', 'カスタム'],
  expression: ['微笑み', '満面の笑顔', '真顔', '無表情', '怒り', '悲しみ', '泣き顔', '驚き', '照れ', '困り顔', 'ウインク', '半目', '閉じ目', 'カスタム'],
  
  // 服装カテゴリ
  clothingCategory: ['現代', 'ファンタジー', '和風', '中華風', 'SF', '制服', 'フォーマル', 'スポーツ'],
  
  // 服装（カテゴリ別）
  clothingByCategory: {
    '現代': {
      tops: ['Tシャツ', 'ロンT', 'シャツ', 'ブラウス', 'ニット', 'セーター', 'カーディガン', 'パーカー', 'スウェット', 'ジャケット', 'ブルゾン', 'コート', 'ダウンジャケット', 'タンクトップ', 'キャミソール', 'チューブトップ', 'ベスト', 'カスタム'],
      bottoms: ['ジーンズ', 'スキニー', 'ストレートパンツ', 'チノパン', 'カーゴパンツ', 'ショートパンツ', 'ハーフパンツ', 'スカート', 'ミニスカート', 'ロングスカート', 'タイトスカート', 'フレアスカート', 'プリーツスカート', 'レギンス', 'スパッツ', 'カスタム'],
      shoes: ['スニーカー', 'ランニングシューズ', 'ローファー', 'パンプス', 'ハイヒール', 'ブーツ', 'ショートブーツ', 'ロングブーツ', 'サンダル', 'ミュール', 'スリッパ', 'カスタム'],
      onepiece: ['ワンピース', 'ロングワンピース', 'シャツワンピース', 'ニットワンピース', 'サロペット', 'オールインワン', 'つなぎ', 'カスタム']
    },
    'ファンタジー': {
      tops: ['チュニック', 'ローブ', '魔法使いのローブ', 'コルセット', 'ビスチェ', '革の胸当て', 'チェインメイル', 'プレートアーマー', '騎士の鎧', 'カスタム'],
      bottoms: ['革のパンツ', '布のズボン', 'ロングスカート', 'ふわふわスカート', 'タイツ', 'レザーレギンス', '鎧の下半身', 'カスタム'],
      shoes: ['革のブーツ', 'ロングブーツ', 'サイハイブーツ', '布の靴', '鉄のブーツ', '魔法の靴', 'カスタム'],
      onepiece: ['ドレス', 'ロングドレス', 'プリンセスドレス', '魔女のドレス', '冒険者の服', '吟遊詩人の衣装', 'カスタム']
    },
    '和風': {
      tops: ['着物', '浴衣', '羽織', '法被', '作務衣', '甚平', '半纏', '陣羽織', '胴着', '忍装束', 'カスタム'],
      bottoms: ['袴', '股引', 'もんぺ', '裁着袴', '馬乗袴', 'カスタム'],
      shoes: ['下駄', '草履', '雪駄', '足袋', 'わらじ', '地下足袋', 'カスタム'],
      onepiece: ['振袖', '訪問着', '留袖', '小紋', '紬', '巫女装束', '白無垢', '十二単', 'カスタム']
    },
    '中華風': {
      tops: ['チャイナブラウス', '唐装', '馬褂', '対襟シャツ', 'カスタム'],
      bottoms: ['チャイナパンツ', 'クンフーパンツ', '馬面裙', 'カスタム'],
      shoes: ['布靴', '刺繍靴', 'カンフーシューズ', 'カスタム'],
      onepiece: ['チャイナドレス', '旗袍', '漢服', '唐装ドレス', '武侠装束', 'カスタム']
    },
    'SF': {
      tops: ['サイバースーツ', 'テックウェア', 'ホログラムジャケット', 'パワードスーツ', 'ボディスーツ', 'メカニカルアーマー', 'ナノファイバースーツ', 'カスタム'],
      bottoms: ['テックパンツ', 'メカニカルレギンス', 'ホログラムスカート', 'カスタム'],
      shoes: ['サイバーブーツ', 'ロケットブーツ', 'メカニカルシューズ', 'ホバーシューズ', 'カスタム'],
      onepiece: ['パイロットスーツ', '宇宙服', 'プラグスーツ', 'エクソスーツ', 'カスタム']
    },
    '制服': {
      tops: ['セーラー服', 'ブレザー', '学ラン', 'シャツ', 'ベスト', 'カーディガン', '軍服', '白衣', 'ナース服', 'カスタム'],
      bottoms: ['プリーツスカート', 'ジャンパースカート', 'スラックス', '制服ズボン', 'カスタム'],
      shoes: ['ローファー', '上履き', '革靴', 'カスタム'],
      onepiece: ['ワンピース制服', 'ジャンパースカート制服', 'つなぎ作業着', 'カスタム']
    },
    'フォーマル': {
      tops: ['スーツジャケット', 'タキシード', 'モーニングコート', 'フロックコート', 'ドレスシャツ', 'カスタム'],
      bottoms: ['スラックス', 'ドレスパンツ', 'ペンシルスカート', 'フォーマルスカート', 'カスタム'],
      shoes: ['革靴', 'オックスフォードシューズ', 'パンプス', 'ドレスシューズ', 'カスタム'],
      onepiece: ['イブニングドレス', 'カクテルドレス', 'パーティードレス', 'ウェディングドレス', 'フォーマルスーツ', 'カスタム']
    },
    'スポーツ': {
      tops: ['スポーツブラ', 'タンクトップ', 'Tシャツ', 'ジャージ', 'トラックジャケット', 'ウィンドブレーカー', 'ユニフォーム', 'カスタム'],
      bottoms: ['ショートパンツ', 'ハーフパンツ', 'ジャージパンツ', 'レギンス', 'スポーツスカート', 'カスタム'],
      shoes: ['ランニングシューズ', 'トレーニングシューズ', 'バスケットシューズ', 'サッカーシューズ', 'テニスシューズ', 'カスタム'],
      onepiece: ['レオタード', '水着', '競泳水着', 'ビキニ', 'ラッシュガード', 'カスタム']
    }
  },
  
  // 服装の色
  clothingColor: ['黒', '白', 'グレー', '赤', '青', '緑', '黄色', 'オレンジ', 'ピンク', '紫', '茶色', 'ベージュ', 'ネイビー', 'カーキ', 'デニム', 'パステルピンク', 'パステルブルー', 'ワインレッド', 'エメラルドグリーン', 'カスタム'],
  
  // アクセサリーカテゴリ  
  accessoryCategory: ['基本', '頭部', '首元', '身体', 'ファンタジー', '和風', '中華風', 'SF'],
  
  // アクセサリー（カテゴリ別）
  accessoriesByCategory: {
    '基本': [
      '眼鏡', 'サングラス', 'コンタクトレンズ', 'モノクル',
      'イヤリング', 'ピアス', 'イヤーカフ',
      '腕時計', 'ブレスレット', 'バングル', 'リストバンド',
      '指輪', 'ネックレス', 'ペンダント', 'ロケット',
      '手袋', 'ミトン', 'アームカバー',
      'ベルト', 'サスペンダー', 'ガーター',
      'ポーチ', 'バッグ', 'リュック',
      'カスタム'
    ],
    '頭部': [
      '帽子', 'キャップ', 'ベレー帽', 'ハット', 'ニット帽',
      'ヘッドホン', 'イヤホン',
      'カチューシャ', 'ヘアバンド', 'ヘアピン', 'ヘアクリップ',
      'リボン', 'シュシュ', 'ヘアゴム',
      '花飾り', '羽飾り',
      'ティアラ', '王冠', 'サークレット',
      'バンダナ', 'ターバン',
      'カスタム'
    ],
    '首元': [
      'ネクタイ', 'ボウタイ', 'リボンタイ', 'アスコットタイ',
      'スカーフ', 'マフラー', 'ストール', 'ショール',
      'チョーカー', '首輪', 'カラー',
      'ネックレス', 'ペンダント',
      'カスタム'
    ],
    '身体': [
      'マント', 'ケープ', 'ポンチョ',
      'エプロン', '前掛け',
      'たすき', 'サッシュ',
      'ハーネス', 'ホルスター',
      '肩章', 'エポレット',
      'バッジ', 'ブローチ', 'コサージュ',
      '包帯', 'ギプス', '眼帯',
      'タトゥー', 'ボディペイント',
      'カスタム'
    ],
    'ファンタジー': [
      '魔法の杖', '魔導書', '水晶玉',
      '魔法使いの帽子', 'とんがり帽子',
      '冠', 'ティアラ', 'サークレット',
      '翼', '天使の輪', '悪魔の角', '悪魔の尻尾',
      'エルフ耳', '獣耳', '猫耳', '狐耳', 'うさ耳',
      '尻尾', '狐の尻尾', '猫の尻尾',
      '仮面', '仮面（半分）', 'ドミノマスク',
      '聖印', 'アミュレット', 'タリスマン',
      '剣', '盾', '弓矢',
      'カスタム'
    ],
    '和風': [
      '簪（かんざし）', '櫛', '笄（こうがい）',
      '髪飾り', '花飾り', '組紐',
      '扇子', '団扇',
      '面', '狐面', '般若面', '能面',
      '手ぬぐい', 'はちまき',
      '風呂敷', '巾着',
      '刀', '脇差', '薙刀',
      '数珠', 'お守り',
      'カスタム'
    ],
    '中華風': [
      '髪飾り', '歩揺', '釵',
      '扇子', '羽扇',
      '玉佩', '香袋',
      '龍の飾り', '鳳凰の飾り',
      '中国結び',
      'カスタム'
    ],
    'SF': [
      'VRゴーグル', 'ARグラス', 'スマートグラス',
      'ホログラムディスプレイ',
      'サイバーアイ', 'メカニカルアーム',
      'パワーグローブ', 'エナジーシールド',
      'ジェットパック', 'アンテナ',
      'LEDアクセサリー', 'ネオンタトゥー',
      'データチップ', 'インプラント',
      'レーザーソード', 'プラズマガン',
      'カスタム'
    ]
  },
  
  // ポーズ
  standing: ['直立', '片足重心', '腕組み', '手を腰に', '片手を上げる', '両手を広げる', 'ガッツポーズ', '敬礼', 'カスタム'],
  gesture: ['ピース', '手を振る', '指差し', 'サムズアップ', 'OK', '手を合わせる', '頬に手を当てる', 'カスタム'],
  viewAngle: ['正面', '斜め前', '横顔', '斜め後ろ', '背中', '見上げる', '見下ろす'],
  
  // 世界観
  worldSetting: ['ファンタジー', '現代', 'SF', '和風', '中華風', '西洋風', 'スチームパンク', 'サイバーパンク', 'ポストアポカリプス', 'カスタム'],
  era: ['古代', '中世', '近世', '近代', '現代', '近未来', '遠未来'],
  location: ['屋内', '屋外', '教室', '街中', '自然', '海', '山', '森', '砂漠', '雪原', '宇宙', '異世界', 'カスタム'],
  atmosphere: ['明るい', '暗い', '幻想的', 'ノスタルジック', 'ミステリアス', 'ロマンチック', 'ダイナミック', '静か', 'カスタム'],
  
  // 画風
  period: [
    '50年代風', '60年代風', '70年代風', '80年代風', '90年代風', 
    '2000年代風', '2010年代風', '現代風',
    'レトロフューチャー', 'ヴィンテージ', 'クラシック', 
    'アールデコ', 'アールヌーヴォー', 'バロック', 'ルネサンス',
    'ビクトリア朝', '大正ロマン', '昭和レトロ', '平成初期',
    'カスタム'
  ],
  technique: [
    '水彩風', '油絵風', 'アクリル画風', 'ガッシュ画風',
    'アニメ塗り', '厚塗り', 'セル画風', 'ギャルゲ塗り',
    '線画', 'ペン画', '鉛筆画', '木炭画', 'パステル画',
    'ドット絵', 'ピクセルアート', '版画風', 'コミック風',
    '3DCG風', '2.5D風', 'ローポリ', 'ハイポリ',
    'フラットデザイン', 'グラデーション塗り', 'モザイク画風',
    'ステンドグラス風', 'エアブラシ', 'スプレーアート',
    'コラージュ', 'ミニマル', 'リアル', 'フォトリアル',
    'カスタム'
  ],
  mood: [
    '明るい', 'ダーク', 'ムーディー', 'ドラマチック',
    'パステル調', 'ビビッド', 'モノクロ', 'セピア',
    '暖色系', '寒色系', '中間色', 'アースカラー',
    'ハイコントラスト', 'ローコントラスト', 
    '彩度高め', '彩度低め', '褪色',
    'ネオン', 'レトロ', 'ノスタルジック', 'サイケデリック',
    'フィルム調', 'ポラロイド風', 'インスタグラム風',
    'シネマティック', 'ファンタジー', 'ダークファンタジー',
    'サイバーパンク', 'スチームパンク', 'ディストピア',
    'カスタム'
  ],
  detailLevel: ['シンプル', '標準', '詳細', '超詳細']
}

export function ManualInputTabs({
  value,
  onChange,
  imageType,
  uploadedImage,
  imagePreview,
  isAnalyzing,
  onImageUpload,
  onImageAnalyze,
  onImageClear
}: ManualInputTabsProps) {
  const [showCustomAccessory, setShowCustomAccessory] = useState(false)
  const [customAccessory, setCustomAccessory] = useState('')
  const [accessorySelectValue, setAccessorySelectValue] = useState('')
  const [clothingCategory, setClothingCategory] = useState<string>('現代')
  const [accessoryCategory, setAccessoryCategory] = useState<string>('基本')
  
  // 保存済みキャラクターとエネミーを取得
  const { characters } = useCharacterStore()
  const { enemies } = useEnemyStore()
  
  const updateField = (path: string, fieldValue: any) => {
    const keys = path.split('.')
    const newValue = { ...value }
    let current: any = newValue
    
    // additionalInfoの場合は特別処理
    if (keys[0] === 'additionalInfo' && !newValue.additionalInfo) {
      newValue.additionalInfo = {}
      current = newValue
    }
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (!current[key]) {
        current[key] = {}
      }
      current = current[key]
    }
    
    // genderの場合は英語に変換
    if (keys[keys.length - 1] === 'gender' && GENDER_MAP[fieldValue as keyof typeof GENDER_MAP]) {
      current[keys[keys.length - 1]] = GENDER_MAP[fieldValue as keyof typeof GENDER_MAP]
    } else {
      current[keys[keys.length - 1]] = fieldValue
    }
    onChange(newValue)
  }
  
  const addToArray = (path: string, item: string) => {
    const keys = path.split('.')
    const newValue = { ...value } as any
    let current = newValue
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      if (!current[key]) {
        current[key] = {}
      }
      current = current[key]
    }
    
    const arrayKey = keys[keys.length - 1]
    if (!current[arrayKey]) {
      current[arrayKey] = []
    }
    if (!current[arrayKey].includes(item)) {
      current[arrayKey] = [...current[arrayKey], item]
    }
    onChange(newValue)
  }
  
  const removeFromArray = (path: string, index: number) => {
    const keys = path.split('.')
    const newValue = { ...value } as any
    let current = newValue
    
    for (let i = 0; i < keys.length - 1; i++) {
      const key = keys[i]
      current = current[key]
    }
    
    const arrayKey = keys[keys.length - 1]
    current[arrayKey] = current[arrayKey].filter((_: any, i: number) => i !== index)
    onChange(newValue)
  }
  
  // 全タブの内容をクリア
  const handleClearAll = () => {
    onChange({})
  }
  
  // ファイル読み込みハンドラー
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    const reader = new FileReader()
    reader.onload = (event) => {
      const content = event.target?.result as string
      updateField('additionalInfo.uploadedFile', content)
    }
    reader.readAsText(file)
    e.target.value = '' // リセット
  }
  
  // 保存済みキャラクター選択ハンドラー
  const handleCharacterSelect = (characterId: string) => {
    if (!characterId || characterId === '_empty') return
    
    // additionalInfoオブジェクトを確実に初期化
    const newAdditionalInfo = {
      ...(value.additionalInfo || {}),
      savedCharacterId: characterId,
      savedEnemyId: undefined
    }
    
    // 新しい値を設定
    const newValue = {
      ...value,
      additionalInfo: newAdditionalInfo
    }
    onChange(newValue)
    
    const character = characters.find(c => c.id === characterId)
    if (character) {
      // キャラクター情報をテキストとして設定
      const characterText = `
【キャラクター情報】
名前: ${character.result.name}
年齢: ${character.result.age}
性別: ${character.result.gender}
概要: ${character.result.summary}

【外見】
${character.result.appearance ? `
全体的な印象: ${character.result.appearance.overallImpression}
身長: ${character.result.appearance.height}
体格: ${character.result.appearance.build}
髪色: ${character.result.appearance.hairColor}
髪型: ${character.result.appearance.hairStyle}
目の色: ${character.result.appearance.eyeColor}
顔の特徴: ${character.result.appearance.facialFeatures}
服装: ${character.result.appearance.clothing}
${character.result.appearance.distinctiveMarks ? `特徴的な印: ${character.result.appearance.distinctiveMarks}` : ''}
` : ''}
      `.trim()
      
      // テキスト情報も追加
      const finalValue = {
        ...newValue,
        additionalInfo: {
          ...newValue.additionalInfo,
          text: characterText
        }
      }
      onChange(finalValue)
    }
  }
  
  // 保存済みエネミー選択ハンドラー
  const handleEnemySelect = (enemyId: string) => {
    if (!enemyId || enemyId === '_empty') return
    
    // additionalInfoオブジェクトを確実に初期化
    const newAdditionalInfo = {
      ...(value.additionalInfo || {}),
      savedEnemyId: enemyId,
      savedCharacterId: undefined
    }
    
    // 新しい値を設定
    const newValue = {
      ...value,
      additionalInfo: newAdditionalInfo
    }
    onChange(newValue)
    
    const enemy = enemies.find(e => e.id === enemyId)
    if (enemy) {
      // エネミー情報をテキストとして設定
      const enemyText = `
【エネミー情報】
名前: ${enemy.result.basicInfo.name}
${enemy.result.basicInfo.title ? `二つ名: ${enemy.result.basicInfo.title}` : ''}
タイプ: ${enemy.result.basicInfo.type}
カテゴリ: ${enemy.result.basicInfo.category}
脅威度: ${enemy.result.basicInfo.threat}

【外見】
${enemy.result.appearance.overallDescription}
サイズ: ${enemy.result.appearance.size}
体型: ${enemy.result.appearance.bodyType}
色調: ${enemy.result.appearance.colorScheme}
${enemy.result.appearance.distinctiveFeatures.length > 0 ? `
特徴:
${enemy.result.appearance.distinctiveFeatures.map(f => `・${f}`).join('\n')}` : ''}

【視覚的インパクト】
${enemy.result.appearance.visualImpact}
${enemy.result.appearance.firstImpressionFear}
      `.trim()
      
      // テキスト情報も追加
      const finalValue = {
        ...newValue,
        additionalInfo: {
          ...newValue.additionalInfo,
          text: enemyText
        }
      }
      onChange(finalValue)
    }
  }
  
  // オブジェクト専用UI
  if (imageType === 'object') {
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>オブジェクト名・種類</Label>
          <Input
            placeholder="例: 魔法の杖、剣、本、宝石、ポーション等"
            value={value.characterName || ''}
            onChange={(e) => updateField('characterName', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>素材</Label>
          <Input
            placeholder="例: 金属、木、ガラス、クリスタル、布、革等"
            value={value.bodyType || ''}
            onChange={(e) => updateField('bodyType', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>主要な色</Label>
          <Input
            placeholder="例: 金色、銀色、青、赤、虹色等"
            value={value.hair?.color || ''}
            onChange={(e) => updateField('hair.color', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>質感・テクスチャ</Label>
          <Input
            placeholder="例: 光沢のある、マット、ざらざら、なめらか、透明等"
            value={value.hair?.texture || ''}
            onChange={(e) => updateField('hair.texture', e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label>装飾・模様</Label>
          <Textarea
            placeholder="例: 宝石がはめ込まれている、ルーン文字が刻まれている、金の装飾等"
            value={value.clothing?.accessories?.join(', ') || ''}
            onChange={(e) => {
              const items = e.target.value.split(',').map(s => s.trim()).filter(s => s)
              updateField('clothing.accessories', items)
            }}
            rows={2}
          />
        </div>

        <SelectWithCustom
          label="世界観"
          value={value.worldSetting?.type}
          onChange={(v) => updateField('worldSetting.type', v)}
          presets={PRESETS.worldSetting}
        />

        <SelectWithCustom
          label="画風"
          value={value.artStyle?.technique}
          onChange={(v) => updateField('artStyle.technique', v)}
          presets={PRESETS.technique}
        />

        <SelectWithCustom
          label="色調・雰囲気"
          value={value.artStyle?.mood}
          onChange={(v) => updateField('artStyle.mood', v)}
          presets={PRESETS.mood}
        />

        <div className="space-y-2">
          <Label>詳細な説明（任意）</Label>
          <Textarea
            placeholder="オブジェクトの詳細な説明を入力（形状、サイズ感、特殊効果等）"
            value={value.freeText || ''}
            onChange={(e) => updateField('freeText', e.target.value)}
            rows={4}
          />
        </div>

        {/* 画像からプロンプト抽出 */}
        <div className="space-y-2 p-4 border rounded-lg bg-blue-50">
          <Label className="text-lg font-semibold">画像からプロンプト抽出</Label>
          <p className="text-sm text-muted-foreground">
            画像をアップロードして、Gemini 2.5 Proで詳細に分析します。
            オブジェクトの形状、素材、色、質感などを細かく抽出します。
          </p>

          <input
            id="image-upload-input"
            type="file"
            accept="image/*"
            onChange={onImageUpload}
            className="hidden"
          />

          {!imagePreview ? (
            <Button
              variant="outline"
              onClick={() => document.getElementById('image-upload-input')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              画像を選択
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="relative border rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-64 w-full object-contain bg-white"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={onImageAnalyze}
                  disabled={isAnalyzing}
                  className="flex-1"
                >
                  {isAnalyzing ? '分析中...' : 'この画像を分析'}
                </Button>
                <Button
                  variant="outline"
                  onClick={onImageClear}
                  disabled={isAnalyzing}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            ※ 分析後、「プロンプトを生成」ボタンを押すと生成結果エリアにプロンプトが表示されます（編集可能）
          </p>
        </div>
      </div>
    )
  }

  // 背景専用UI
  if (imageType === 'background') {
    return (
      <div className="space-y-4">
        <SelectWithCustom
          label="場所・環境"
          value={value.worldSetting?.location}
          onChange={(v) => updateField('worldSetting.location', v)}
          presets={PRESETS.location}
        />
        <SelectWithCustom
          label="雰囲気"
          value={value.worldSetting?.atmosphere}
          onChange={(v) => updateField('worldSetting.atmosphere', v)}
          presets={PRESETS.atmosphere}
        />

        <div className="space-y-2">
          <Label>時代</Label>
          <Select value={value.worldSetting?.era || ''} onValueChange={(v) => updateField('worldSetting.era', v)}>
            <SelectTrigger>
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.era.map(item => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>詳細な説明（任意）</Label>
          <Textarea
            placeholder="背景の詳細な説明を入力"
            value={value.freeText || ''}
            onChange={(e) => updateField('freeText', e.target.value)}
            rows={3}
          />
        </div>
      </div>
    )
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={handleClearAll}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          全タブクリア
        </Button>
      </div>
      
      <Tabs defaultValue="basic">
        <TabsList className="grid grid-cols-3 md:grid-cols-5 gap-1 w-full">
          <TabsTrigger value="basic">基本</TabsTrigger>
          <TabsTrigger value="appearance">外見</TabsTrigger>
          <TabsTrigger value="clothing">服装</TabsTrigger>
          <TabsTrigger value="style">画風</TabsTrigger>
          <TabsTrigger value="additional">追加情報</TabsTrigger>
        </TabsList>
      
      <TabsContent value="basic" className="space-y-4">
        <div className="space-y-2">
          <Label>性別</Label>
          <Select value={Object.entries(GENDER_MAP).find(([_, v]) => v === value.gender)?.[0] || ''} onValueChange={(v) => updateField('gender', v)}>
            <SelectTrigger>
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.gender.map(item => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>年齢層</Label>
          <Select value={value.ageGroup || ''} onValueChange={(v) => updateField('ageGroup', v)}>
            <SelectTrigger>
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.ageGroup.map(item => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <SelectWithCustom
          label="体型"
          value={value.bodyType}
          onChange={(v) => updateField('bodyType', v)}
          presets={PRESETS.bodyType}
        />
        
        {value.bodyType === 'カスタム' && (
          <div className="space-y-2">
            <Label>体型の詳細（胸の大きさなど）</Label>
            <Textarea
              placeholder="例: スレンダーで胸が大きい、筋肉質で肩幅が広い"
              value={value.bodyTypeDetail || ''}
              onChange={(e) => updateField('bodyTypeDetail', e.target.value)}
              rows={2}
            />
          </div>
        )}
        
        <SelectWithCustom
          label="表情"
          value={value.expression}
          onChange={(v) => updateField('expression', v)}
          presets={PRESETS.expression}
        />
        
        {imageType === 'illustration' && (
          <>
            <SelectWithCustom
              label="ポーズ"
              value={value.pose?.standing}
              onChange={(v) => updateField('pose.standing', v)}
              presets={PRESETS.standing}
            />
            <SelectWithCustom
              label="ジェスチャー"
              value={value.pose?.gesture}
              onChange={(v) => updateField('pose.gesture', v)}
              presets={PRESETS.gesture}
            />
            
            <div className="space-y-2">
              <Label>視点・アングル</Label>
              <Select value={value.pose?.viewAngle || ''} onValueChange={(v) => updateField('pose.viewAngle', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="選択してください" />
                </SelectTrigger>
                <SelectContent>
                  {PRESETS.viewAngle.map(item => (
                    <SelectItem key={item} value={item}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </>
        )}
      </TabsContent>
      
      <TabsContent value="appearance" className="space-y-4">
        <SelectWithCustom
          label="前髪"
          value={value.hair?.front}
          onChange={(v) => updateField('hair.front', v)}
          presets={PRESETS.hairFront}
        />
        <SelectWithCustom
          label="髪型"
          value={value.hair?.style}
          onChange={(v) => updateField('hair.style', v)}
          presets={PRESETS.hairStyle}
        />
        <SelectWithCustom
          label="髪質"
          value={value.hair?.texture}
          onChange={(v) => updateField('hair.texture', v)}
          presets={PRESETS.hairTexture}
        />
        <SelectWithCustom
          label="髪色"
          value={value.hair?.color}
          onChange={(v) => updateField('hair.color', v)}
          presets={PRESETS.hairColor}
        />
        <SelectWithCustom
          label="色効果"
          value={value.hair?.colorEffect}
          onChange={(v) => updateField('hair.colorEffect', v)}
          presets={PRESETS.hairColorEffect}
        />
        
        <SelectWithCustom
          label="目の形"
          value={value.eyes?.shape}
          onChange={(v) => updateField('eyes.shape', v)}
          presets={PRESETS.eyeShape}
        />
        
        <div className="space-y-2">
          <Label>目の大きさ</Label>
          <Select value={value.eyes?.size || ''} onValueChange={(v) => updateField('eyes.size', v)}>
            <SelectTrigger>
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.eyeSize.map(item => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <SelectWithCustom
          label="目の色"
          value={value.eyes?.color}
          onChange={(v) => updateField('eyes.color', v)}
          presets={PRESETS.eyeColor}
        />
      </TabsContent>
      
      <TabsContent value="clothing" className="space-y-4">
        <div className="space-y-2">
          <Label>服装カテゴリ</Label>
          <Select value={clothingCategory} onValueChange={setClothingCategory}>
            <SelectTrigger>
              <SelectValue placeholder="カテゴリを選択" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.clothingCategory.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {PRESETS.clothingByCategory[clothingCategory]?.onepiece && (
          <>
            <SelectWithCustom
              label="ワンピース系"
              value={value.clothing?.onepiece}
              onChange={(v) => {
                updateField('clothing.onepiece', v)
                // ワンピースを選択したらトップスとボトムスをクリア
                if (v) {
                  updateField('clothing.tops', undefined)
                  updateField('clothing.topsColor', undefined)
                  updateField('clothing.bottoms', undefined)
                  updateField('clothing.bottomsColor', undefined)
                }
              }}
              presets={PRESETS.clothingByCategory[clothingCategory].onepiece}
            />
            {value.clothing?.onepiece && (
              <>
                <SelectWithCustom
                  label="ワンピースの色"
                  value={value.clothing?.onepieceColor}
                  onChange={(v) => updateField('clothing.onepieceColor', v)}
                  presets={PRESETS.clothingColor}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateField('clothing.onepiece', undefined)
                    updateField('clothing.onepieceColor', undefined)
                  }}
                >
                  ワンピースをクリア
                </Button>
              </>
            )}
          </>
        )}
        
        {!value.clothing?.onepiece && (
          <>
            <SelectWithCustom
              label="トップス"
              value={value.clothing?.tops}
              onChange={(v) => {
                updateField('clothing.tops', v)
                // トップスを選択したらワンピースをクリア
                if (v) {
                  updateField('clothing.onepiece', undefined)
                  updateField('clothing.onepieceColor', undefined)
                }
              }}
              presets={PRESETS.clothingByCategory[clothingCategory]?.tops || []}
            />
            <SelectWithCustom
              label="トップスの色"
              value={value.clothing?.topsColor}
              onChange={(v) => updateField('clothing.topsColor', v)}
              presets={PRESETS.clothingColor}
            />
            <SelectWithCustom
              label="ボトムス"
              value={value.clothing?.bottoms}
              onChange={(v) => updateField('clothing.bottoms', v)}
              presets={PRESETS.clothingByCategory[clothingCategory]?.bottoms || []}
            />
            <SelectWithCustom
              label="ボトムスの色"
              value={value.clothing?.bottomsColor}
              onChange={(v) => updateField('clothing.bottomsColor', v)}
              presets={PRESETS.clothingColor}
            />
          </>
        )}
        
        <SelectWithCustom
          label="靴"
          value={value.clothing?.shoes}
          onChange={(v) => updateField('clothing.shoes', v)}
          presets={PRESETS.clothingByCategory[clothingCategory]?.shoes || []}
        />
        <SelectWithCustom
          label="靴の色"
          value={value.clothing?.shoesColor}
          onChange={(v) => updateField('clothing.shoesColor', v)}
          presets={PRESETS.clothingColor}
        />
        
        <div className="space-y-2">
          <Label>アクセサリーカテゴリ</Label>
          <Select value={accessoryCategory} onValueChange={setAccessoryCategory}>
            <SelectTrigger>
              <SelectValue placeholder="カテゴリを選択" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.accessoryCategory.map(cat => (
                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>アクセサリー</Label>
          <Select 
            value={accessorySelectValue}
            onValueChange={(v) => {
              if (v === 'カスタム') {
                setShowCustomAccessory(true)
                setAccessorySelectValue('')  // Selectをリセット
              } else {
                addToArray('clothing.accessories', v)
                setAccessorySelectValue('')  // Selectをリセット
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="追加するアクセサリーを選択" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.accessoriesByCategory[accessoryCategory]?.map((item: string) => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {showCustomAccessory && (
            <div className="flex gap-2">
              <Input
                placeholder="カスタムアクセサリーを入力"
                value={customAccessory}
                onChange={(e) => setCustomAccessory(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && customAccessory.trim()) {
                    addToArray('clothing.accessories', customAccessory.trim())
                    setCustomAccessory('')
                    setShowCustomAccessory(false)
                  }
                }}
              />
              <Button
                size="sm"
                onClick={() => {
                  if (customAccessory.trim()) {
                    addToArray('clothing.accessories', customAccessory.trim())
                    setCustomAccessory('')
                    setShowCustomAccessory(false)
                  }
                }}
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setCustomAccessory('')
                  setShowCustomAccessory(false)
                }}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {value.clothing?.accessories?.map((item, i) => (
              <Badge key={i} variant="secondary" className="cursor-pointer" onClick={() => removeFromArray('clothing.accessories', i)}>
                {item} <X className="ml-1 h-3 w-3" />
              </Badge>
            ))}
          </div>
        </div>
      </TabsContent>
      
      <TabsContent value="style" className="space-y-4">
        <SelectWithCustom
          label="世界観"
          value={value.worldSetting?.type}
          onChange={(v) => updateField('worldSetting.type', v)}
          presets={PRESETS.worldSetting}
        />
        <SelectWithCustom
          label="場所"
          value={value.worldSetting?.location}
          onChange={(v) => updateField('worldSetting.location', v)}
          presets={PRESETS.location}
        />
        <SelectWithCustom
          label="雰囲気"
          value={value.worldSetting?.atmosphere}
          onChange={(v) => updateField('worldSetting.atmosphere', v)}
          presets={PRESETS.atmosphere}
        />
        
        <SelectWithCustom
          label="時代感"
          value={value.artStyle?.period}
          onChange={(v) => updateField('artStyle.period', v)}
          presets={PRESETS.period}
        />
        <SelectWithCustom
          label="画法"
          value={value.artStyle?.technique}
          onChange={(v) => updateField('artStyle.technique', v)}
          presets={PRESETS.technique}
        />
        <SelectWithCustom
          label="色調"
          value={value.artStyle?.mood}
          onChange={(v) => updateField('artStyle.mood', v)}
          presets={PRESETS.mood}
        />
        
        <div className="space-y-2">
          <Label>カスタム画風（作品風・絵師風など自己責任で入力）</Label>
          <Textarea
            placeholder="例: 京都アニメーション風、新海誠風、ジブリ風など"
            value={value.artStyle?.customStyle || ''}
            onChange={(e) => updateField('artStyle.customStyle', e.target.value)}
            rows={2}
          />
          <p className="text-xs text-muted-foreground">※著作権等には十分ご注意ください</p>
        </div>
        
        <div className="space-y-2">
          <Label>詳細度</Label>
          <Select value={value.artStyle?.detailLevel || ''} onValueChange={(v) => updateField('artStyle.detailLevel', v)}>
            <SelectTrigger>
              <SelectValue placeholder="選択してください" />
            </SelectTrigger>
            <SelectContent>
              {PRESETS.detailLevel.map(item => (
                <SelectItem key={item} value={item}>{item}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </TabsContent>
      
      <TabsContent value="additional" className="space-y-4">
        <div className="space-y-2">
          <Label>保存済みキャラクターから読み込み</Label>
          <Select 
            value={value.additionalInfo?.savedCharacterId || ''}
            onValueChange={handleCharacterSelect}
          >
            <SelectTrigger>
              <SelectValue placeholder="キャラクターを選択" />
            </SelectTrigger>
            <SelectContent>
              {characters.length === 0 ? (
                <SelectItem value="_empty" disabled>
                  保存済みキャラクターがありません
                </SelectItem>
              ) : (
                characters.map(char => (
                  <SelectItem key={char.id} value={char.id}>
                    {char.result.name} - {char.title}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>保存済みエネミーから読み込み</Label>
          <Select 
            value={value.additionalInfo?.savedEnemyId || ''}
            onValueChange={handleEnemySelect}
          >
            <SelectTrigger>
              <SelectValue placeholder="エネミーを選択" />
            </SelectTrigger>
            <SelectContent>
              {enemies.length === 0 ? (
                <SelectItem value="_empty" disabled>
                  保存済みエネミーがありません
                </SelectItem>
              ) : (
                enemies.map(enemy => (
                  <SelectItem key={enemy.id} value={enemy.id}>
                    {enemy.result.basicInfo.name} - {enemy.title}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        </div>
        
        {/* エネミーまたはキャラクターが選択されている場合のビュー選択 */}
        {(value.additionalInfo?.savedCharacterId || value.additionalInfo?.savedEnemyId) ? (
          <div className="space-y-2">
            <Label>エネミー/戦闘用ビュー設定</Label>
            <Select 
              value={value.additionalInfo?.enemyView || 'none'}
              onValueChange={(v) => updateField('additionalInfo.enemyView', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="ビューを選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">通常（非戦闘）</SelectItem>
                <SelectItem value="front">全身フロントビュー（戦闘）</SelectItem>
                <SelectItem value="side">全身サイドビュー右向き（戦闘）</SelectItem>
              </SelectContent>
            </Select>
            {value.additionalInfo?.enemyView && value.additionalInfo.enemyView !== 'none' && (
              <p className="text-xs text-muted-foreground">
                ※ 戦闘モードで生成されます。戦闘に相応しいポーズやエフェクトが追加されます。
              </p>
            )}
          </div>
        ) : null}
        
        {/* 戦闘用グラフィックの絵柄プリセット */}
        {value.additionalInfo?.enemyView && value.additionalInfo.enemyView !== 'none' && (
          <div className="space-y-2">
            <Label>戦闘用グラフィック絵柄プリセット</Label>
            <Select 
              value={value.additionalInfo?.battleGraphicStyle || 'none'}
              onValueChange={(v) => updateField('additionalInfo.battleGraphicStyle', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="絵柄を選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし（デフォルト）</SelectItem>
                <SelectItem value="rpgmaker">RPGツクール風</SelectItem>
                <SelectItem value="final_fantasy">ファイナルファンタジー風</SelectItem>
                <SelectItem value="dragon_quest">ドラゴンクエスト風</SelectItem>
                <SelectItem value="pokemon">ポケモン風</SelectItem>
                <SelectItem value="fighting_game">格闘ゲーム風</SelectItem>
                <SelectItem value="action_game">アクションゲーム風</SelectItem>
                <SelectItem value="srpg">SRPG風（スーパーロボット大戦等）</SelectItem>
                <SelectItem value="retro_pixel">レトロゲーム風（ドット絵）</SelectItem>
                <SelectItem value="tales">テイルズシリーズ風</SelectItem>
                <SelectItem value="persona">ペルソナシリーズ風</SelectItem>
                <SelectItem value="fire_emblem">ファイアーエムブレム風</SelectItem>
                <SelectItem value="xenoblade">ゼノブレイド風</SelectItem>
                <SelectItem value="megaten">現代ホラー風（女神転生等）</SelectItem>
              </SelectContent>
            </Select>
            {value.additionalInfo?.battleGraphicStyle && value.additionalInfo.battleGraphicStyle !== 'none' && (
              <p className="text-xs text-muted-foreground">
                ※ 選択した絵柄の特徴を反映したプロンプトが生成されます。
              </p>
            )}
          </div>
        )}

        {/* 画像からプロンプト抽出 */}
        <div className="space-y-2 p-4 border rounded-lg bg-blue-50">
          <Label className="text-lg font-semibold">画像からプロンプト抽出</Label>
          <p className="text-sm text-muted-foreground">
            画像をアップロードして、Gemini 2.5 Proで詳細に分析します。
            髪型（前髪・横髪・後ろ髪）、表情、ポーズ、服装などを細かく抽出します。
          </p>

          <input
            id="image-upload-input"
            type="file"
            accept="image/*"
            onChange={onImageUpload}
            className="hidden"
          />

          {!imagePreview ? (
            <Button
              variant="outline"
              onClick={() => document.getElementById('image-upload-input')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              画像を選択
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="relative border rounded-lg overflow-hidden">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-h-64 w-full object-contain bg-white"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={onImageAnalyze}
                  disabled={isAnalyzing}
                  className="flex-1"
                >
                  {isAnalyzing ? '分析中...' : 'この画像を分析'}
                </Button>
                <Button
                  variant="outline"
                  onClick={onImageClear}
                  disabled={isAnalyzing}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            ※ 分析後、「プロンプトを生成」ボタンを押すと生成結果エリアにプロンプトが表示されます（編集可能）
          </p>
        </div>

        <div className="space-y-2">
          <Label>テキストファイルから読み込み</Label>
          <input
            id="additional-file-upload"
            type="file"
            accept=".txt"
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => document.getElementById('additional-file-upload')?.click()}
          >
            <Upload className="h-4 w-4 mr-2" />
            ファイルを選択
          </Button>
          {value.additionalInfo?.uploadedFile && (
            <div className="mt-2 p-2 bg-muted rounded text-sm">
              <FileText className="h-4 w-4 inline mr-1" />
              ファイルが読み込まれました
            </div>
          )}
        </div>
        
        <div className="space-y-2">
          <Label>追加情報（エルフ、亜人、特殊な種族など）</Label>
          <Textarea
            placeholder="例: エルフの耳を持つ、狐の尻尾がある、翼が生えている、角がある、鱗がある、など"
            value={value.additionalInfo?.text || ''}
            onChange={(e) => updateField('additionalInfo.text', e.target.value)}
            rows={8}
          />
          <p className="text-xs text-muted-foreground">
            ※ 他のタブで設定した内容が優先されます。ここで入力した外見要素は補足として使用されます。
          </p>
        </div>
      </TabsContent>
    </Tabs>
    </div>
  )
}